"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowDownToLine, ArrowUpFromLine, Copy, Check, ShieldCheck } from "lucide-react";
import { StatusPill } from "@/components/ui";
import { CurrencyBadge } from "@/components/currency";
import { ChainBadge, chainName } from "@/components/chain";
import { fmtAmount } from "@/lib/format";
import DepositInstructions from "@/components/DepositInstructions";
import type { VirtualAccount } from "@/lib/conduit";

type Instruction = NonNullable<VirtualAccount["depositInstructions"]>[number];

/** USD virtual account card: flag-first identity, deposit instructions and withdrawals behind explicit actions. */
export function VaCard({
  id,
  code,
  status,
  available,
  pending,
  frozen,
  instructions,
}: {
  id: string;
  code: string;
  status?: string;
  available?: string;
  pending?: string;
  frozen?: string;
  instructions: Instruction[];
}) {
  const [showDeposit, setShowDeposit] = useState(false);

  return (
    <div className="card-dark rise p-6">
      <div className="mb-4 flex items-center gap-2.5">
        <CurrencyBadge code={code} size={28} />
        <div className="min-w-0 flex-1">
          <div className="eyebrow" style={{ color: "var(--c-on-dark-soft)" }}>
            {code} virtual account
          </div>
          <div className="display text-[28px]">{available !== undefined ? fmtAmount(available, code) : "0.00"}</div>
        </div>
        <StatusPill status={status} />
      </div>

      {(pending !== undefined || frozen !== undefined) && (
        <div className="mb-4 flex gap-5 text-[13px]" style={{ color: "var(--c-on-dark-soft)" }}>
          <span>pending {fmtAmount(pending)}</span>
          <span>frozen {fmtAmount(frozen)}</span>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          className="btn btn-primary !px-5 !py-2 text-[14px]"
          onClick={() => setShowDeposit((s) => !s)}
        >
          <ArrowDownToLine size={15} /> Deposit
        </button>
        <Link
          href={`/payouts?source=${encodeURIComponent(`va:${id}`)}`}
          className="btn !px-5 !py-2 text-[14px]"
          style={{ background: "transparent", color: "var(--c-white)", boxShadow: "inset 0 0 0 1px var(--c-on-dark-hairline)" }}
        >
          <ArrowUpFromLine size={15} /> Withdraw
        </Link>
      </div>

      {showDeposit && (
        <div className="mt-4">
          <DepositInstructions instructions={instructions} defaultExpanded />
        </div>
      )}

      <div className="mono mt-4 text-[12px]" style={{ color: "var(--c-on-dark-soft)" }}>
        {id}
      </div>
    </div>
  );
}

/** Crypto wallet card: chain-first identity, per-asset balances, deposit address + withdrawal entry. */
export function WalletCard({
  id,
  chain,
  address,
  status,
  custody,
  balances,
  withdrawSourceValue,
}: {
  id: string;
  chain: string;
  address: string | null;
  status?: string;
  custody?: string;
  balances: Array<{ code: string; available?: string; pending?: string; frozen?: string }>;
  /** Funding-source key (`w:{id}:{code}`) the payout wizard should preselect for withdrawals. */
  withdrawSourceValue: string;
}) {
  const [showDeposit, setShowDeposit] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <div className="card rise p-6">
      <div className="mb-4 flex items-center gap-2.5">
        <ChainBadge chain={chain} size={28} />
        <div className="min-w-0 flex-1">
          <div className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
            {chainName(chain)} wallet
          </div>
          <div className="mono truncate text-[13px]" style={{ color: "var(--c-ink-soft)" }}>
            {address ?? "provisioning…"}
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="mb-4 flex gap-3">
        <button
          type="button"
          className="btn btn-dark !px-5 !py-2 text-[14px]"
          disabled={!address}
          onClick={() => setShowDeposit((s) => !s)}
        >
          <ArrowDownToLine size={15} /> Deposit
        </button>
        <Link
          href={`/payouts?source=${encodeURIComponent(withdrawSourceValue)}`}
          className="btn btn-ghost !px-5 !py-2 text-[14px]"
        >
          <ArrowUpFromLine size={15} /> Withdraw
        </Link>
      </div>

      {showDeposit && address && (
        <div className="card-cream mb-4 p-4">
          <div className="eyebrow mb-2" style={{ color: "var(--c-ink-soft)" }}>
            deposit address · {chainName(chain)}
          </div>
          <button
            type="button"
            className="group flex w-full items-center gap-2 text-left"
            title="Copy address"
            onClick={() => {
              navigator.clipboard.writeText(address);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            <span className="mono min-w-0 break-all text-[13px]">{address}</span>
            {copied ? (
              <Check size={14} className="shrink-0" style={{ color: "var(--c-green)" }} />
            ) : (
              <Copy size={14} className="shrink-0 opacity-40 transition-opacity duration-150 group-hover:opacity-100" />
            )}
          </button>
          <p className="mt-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            Send only assets issued on {chainName(chain)} to this address — deposits from other networks can't be
            recovered.
          </p>
        </div>
      )}

      {/* One chain, many assets: an ethereum wallet can hold USDC and USDT
          side by side — each is its own balance line. */}
      <div className="eyebrow mb-2" style={{ color: "var(--c-ink-soft)" }}>
        balances by asset
      </div>
      {balances.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>asset</th>
              <th>available</th>
              <th>pending</th>
              <th>frozen</th>
            </tr>
          </thead>
          <tbody>
            {balances.map((b, i) => (
              <tr key={`${b.code}-${i}`}>
                <td>
                  <span className="flex items-center gap-2 font-normal">
                    <CurrencyBadge code={b.code} size={18} /> {b.code}
                  </span>
                </td>
                <td className="font-normal">{fmtAmount(b.available, b.code)}</td>
                <td style={{ color: "var(--c-ink-soft)" }}>{fmtAmount(b.pending, b.code)}</td>
                <td style={{ color: "var(--c-ink-soft)" }}>{fmtAmount(b.frozen, b.code)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
          Nothing held yet — hit Deposit to get this wallet's address. Each asset it receives gets its own balance
          line here.
        </p>
      )}

      <div className="mono mt-4 flex items-center gap-1.5 text-[11px]" style={{ color: "var(--c-ink-soft)" }}>
        {custody === "non_custodial" && <ShieldCheck size={12} />}
        {id} · {custody?.replaceAll("_", " ") ?? ""}
      </div>
    </div>
  );
}
